import { useLocalObservable } from 'mobx-react-lite';
import { runInAction } from 'mobx';
import { updateCameraOwner } from '@/models/camera/service';
import { searchAccounts } from '@/models/clients/service';
import type { AccountSearch } from '@/models/clients/types';

export function useOwnerModal(idAccount: string | undefined, reloadServices: () => void) {
  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  const store = useLocalObservable(() => ({
    isOpen: false,
    idCamera: '',
    cameraName: '',
    searchQuery: '',
    searchResults: [] as AccountSearch[],
    isSearching: false,
    selectedAccount: null as AccountSearch | null,
    moveInVc: false,
    isLoading: false,
    error: null as string | null,
  }));

  function open(idCamera: string, cameraName: string) {
    runInAction(() => {
      store.isOpen = true;
      store.idCamera = idCamera;
      store.cameraName = cameraName;
      store.searchQuery = '';
      store.searchResults = [];
      store.isSearching = false;
      store.selectedAccount = null;
      store.moveInVc = false;
      store.error = null;
    });
  }

  function close() {
    runInAction(() => {
      store.isOpen = false;
    });
  }

  function setSearchQuery(value: string) {
    runInAction(() => {
      store.searchQuery = value;
      store.selectedAccount = null;
      store.searchResults = [];
    });
    if (searchTimer) clearTimeout(searchTimer);
    if (!value.trim()) return;
    searchTimer = setTimeout(() => {
      runInAction(() => {
        store.isSearching = true;
      });
      searchAccounts(value.trim())
        .then((results) =>
          runInAction(() => {
            store.searchResults = results;
            store.isSearching = false;
          })
        )
        .catch(() =>
          runInAction(() => {
            store.isSearching = false;
          })
        );
    }, 300);
  }

  function selectAccount(account: AccountSearch) {
    runInAction(() => {
      store.selectedAccount = account;
      store.searchQuery = account.login;
      store.searchResults = [];
    });
  }

  function clearSelection() {
    runInAction(() => {
      store.selectedAccount = null;
      store.searchQuery = '';
      store.searchResults = [];
    });
  }

  function setMoveInVc(value: boolean) {
    runInAction(() => {
      store.moveInVc = value;
    });
  }

  function submit() {
    if (!idAccount || !store.selectedAccount) return;
    runInAction(() => {
      store.isLoading = true;
      store.error = null;
    });
    updateCameraOwner(store.idCamera, {
      from_id_account: idAccount,
      to_id_account: store.selectedAccount.id_account,
      move_in_vc: store.moveInVc ? '1' : '0',
    })
      .then(() => {
        runInAction(() => {
          store.isOpen = false;
          store.isLoading = false;
        });
        reloadServices();
      })
      .catch((err: Error) =>
        runInAction(() => {
          store.error = err.message;
          store.isLoading = false;
        })
      );
  }

  return {
    get isOpen() {
      return store.isOpen;
    },
    get idCamera() {
      return store.idCamera;
    },
    get cameraName() {
      return store.cameraName;
    },
    get searchQuery() {
      return store.searchQuery;
    },
    get searchResults() {
      return store.searchResults;
    },
    get isSearching() {
      return store.isSearching;
    },
    get selectedAccount() {
      return store.selectedAccount;
    },
    get moveInVc() {
      return store.moveInVc;
    },
    get isLoading() {
      return store.isLoading;
    },
    get error() {
      return store.error;
    },
    open,
    close,
    setSearchQuery,
    selectAccount,
    clearSelection,
    setMoveInVc,
    submit,
  };
}

export type OwnerModal = ReturnType<typeof useOwnerModal>;
